<script lang="ts" setup>
import dayjs from 'dayjs'
import type { TabLink } from '~/components/tabs/Tabs.vue'
import type { Data } from '~~/server/api/documents/index'

export interface PagesDocumentsTabsProps {
    data: Data
}
export interface PagesDocumentsTabsEmitsValue {
    value: string
    key: string
}
export interface PagesDocumentsTabsEmits {
    (e: 'date-update', value: PagesDocumentsTabsEmitsValue): void
}

const props = defineProps<PagesDocumentsTabsProps>()
const emit = defineEmits<PagesDocumentsTabsEmits>()
const config = useRuntimeConfig()
const { $t } = useNuxtApp()
const tabLinks = computed<TabLink[]>(() => {
    // set default tab links
    const links: TabLink[] = [
        {
            name: $t('Aktuálne dokumenty'),
        },
    ]
    // is archive for the top level category enabled?
    if (!props.data.category_level_0.not_show_archive) {
        links.push({
            name: $t('Archív'),
        })
    }
    // return tab links
    return links
})

const isArchiveEmpty = computed<boolean>(() => {
    if (!props.data) return true

    return !props.data.documents.some(
        (group) => group.archive_papers.length > 0
    )
})
const areDocumentsEmpty = computed<boolean>(() => {
    if (!props.data) return true

    return !props.data.documents.some((group) => group.actual_papers.length > 0)
})

const categoryTitle = computed((): string => {
    const title =
        props.data.category_level_2?.title ||
        props.data.category_level_1?.title ||
        props.data.category_level_0?.title

    return title
})
</script>
<template>
  <section
    :class="{
      'pt-4 pt-md-6 border-top': props.data.category_level_1?.title,
    }"
  >
    <p
      v-if="props.data.category_level_1?.title"
      class="text-black zeta fw-bold mb-2"
    >
      {{ props.data.category_level_0.title }}{{ ':' }}
    </p>
    <h2 class="text-secondary mb-4">
      {{ categoryTitle }}
    </h2>

    <Tabs
      :links="tabLinks"
      tabs-id="PagesDocumentsTabs"
    >
      <template #tab-panel-0>
        <Tracker
          :data="{
            position: props.data.category_level_0.title,
            title: categoryTitle,
          }"
        >
          <Accordion>
            <template
              v-for="(group, groupIndex) in props.data.documents"
              :key="`insurace-document-actual-${groupIndex}`"
            >
              <AccordionItem
                v-if="group.actual_papers.length > 0"
                color="secondary"
                :btn-text="group.category.title"
                :btn-index="0"
              >
                <DocumentsTable :data="group.actual_papers" />
              </AccordionItem>
            </template>
          </Accordion>
        </Tracker>
        <PagesDocumentsEmpty v-if="areDocumentsEmpty" />
      </template>
      <template
        v-if="!props.data.category_level_0.not_show_archive"
        #tab-panel-1
      >
        <PagesDocumentsSearch
          @update="
            (value: string) =>
              emit('date-update', {
                value: value
                  ? dayjs(value).format(
                    config.public.CHART_DATE_FORMAT
                  )
                  : value,
                key: 'do',
              })
          "
        />
        <Accordion v-if="!isArchiveEmpty">
          <template
            v-for="(group, groupIndex) in props.data.documents"
            :key="`insurace-document-archive-${groupIndex}`"
          >
            <AccordionItem
              v-if="group.archive_papers.length > 0"
              color="secondary"
              :btn-text="group.category.title"
              :btn-index="0"
            >
              <DocumentsTable :data="group.archive_papers" />
            </AccordionItem>
          </template>
        </Accordion>
        <PagesDocumentsEmpty v-else />
      </template>
    </Tabs>
  </section>
</template>
